/**
 * useTutorialNarration — plays each tour step's narration via the backend
 * ElevenLabs proxy (POST /ai/tts, same voice as chat read-aloud) and reports
 * when audio ends so the tour can auto-advance. Fails SILENTLY: a welcome
 * experience must never show an error wall — narration just goes quiet.
 */
import { useEffect, useRef, useState } from "react";
import type { TutorialStep } from "./types";

interface UseTutorialNarrationArgs {
  step: TutorialStep | null;
  isActive: boolean;
  enabled: boolean;
  onAudioEnded: () => void;
}

// The default ElevenLabs delivery is too leisurely for a guided tour; browsers
// preserve pitch at this rate so it sounds natural, just brisker.
const NARRATION_PLAYBACK_RATE = 1.1;

export function useTutorialNarration({
  step,
  isActive,
  enabled,
  onAudioEnded,
}: UseTutorialNarrationArgs) {
  const cacheRef = useRef<Map<string, string>>(new Map());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isNarrating, setIsNarrating] = useState(false);
  // Keep the latest callback without retriggering the play effect
  const onEndedRef = useRef(onAudioEnded);
  onEndedRef.current = onAudioEnded;

  useEffect(() => {
    if (!isActive || !enabled || !step) return;
    let cancelled = false;

    const play = async () => {
      try {
        const text = step.narrationText || step.description;
        let url = cacheRef.current.get(step.id);
        if (!url) {
          const apiBase = (
            process.env.REACT_APP_API_URL || "https://api.autumnplatform.com"
          ).replace(/\/$/, "");
          const res = await fetch(`${apiBase}/ai/tts`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
            },
            body: JSON.stringify({ text }),
          });
          if (!res.ok) throw new Error(`TTS failed: ${res.status}`);
          url = URL.createObjectURL(await res.blob());
          cacheRef.current.set(step.id, url);
        }
        if (cancelled) return;
        const audio = new Audio(url);
        audio.playbackRate = NARRATION_PLAYBACK_RATE;
        audioRef.current = audio;
        audio.onended = () => {
          setIsNarrating(false);
          onEndedRef.current();
        };
        audio.onerror = () => setIsNarrating(false);
        setIsNarrating(true);
        await audio.play();
      } catch (err) {
        console.warn("[Tutorial narration] unavailable, continuing silently:", err);
        setIsNarrating(false);
      }
    };

    void play();
    return () => {
      cancelled = true;
      audioRef.current?.pause();
      audioRef.current = null;
      setIsNarrating(false);
    };
  }, [step?.id, isActive, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revoke cached blob URLs when the tour ends
  useEffect(() => {
    if (isActive) return;
    cacheRef.current.forEach((url) => URL.revokeObjectURL(url));
    cacheRef.current.clear();
  }, [isActive]);

  return { isNarrating };
}
